/**
 * Resources index page — grid of Blog / Docs / Case Studies / Webinars / Release Notes.
 */
import { Link } from "react-router-dom";
import {
  BookOpen, FileText, Trophy, Video, ScrollText, ArrowRight, Sparkles, ArrowUpRight,
} from "lucide-react";
import { MarketingHeader, MarketingFooter } from "../MarketingChrome";

const RESOURCES = [
  {
    icon: BookOpen,
    slug: "blog",
    title: "Blog",
    tagline: "Field notes and product deep-dives.",
    desc: "How deterministic engines actually work, the anatomy of an evidence-cited AI recommendation, and playbooks from real operators.",
  },
  {
    icon: FileText,
    slug: "docs",
    title: "Product Docs",
    tagline: "How to run each engine, step by step.",
    desc: "API reference, workspace guides, integration setup (Stripe, DocuSign, Twilio, Resend), and admin console operations.",
  },
  {
    icon: Trophy,
    slug: "case-studies",
    title: "Case Studies",
    tagline: "Real operators. Real numbers. Real deals.",
    desc: "How wholesalers cut underwriting from 90 to 6 minutes, how funds replaced 3 BI dashboards, and how BRRRR operators tripled cycles.",
    primary: true,
  },
  {
    icon: Video,
    slug: "webinars",
    title: "Webinars",
    tagline: "Live sessions with acquisition experts.",
    desc: "Monthly deep-dives on Sub-To, Novation, Wholetail, and portfolio strategies — with Q&A and archived recordings.",
  },
  {
    icon: ScrollText,
    slug: "release-notes",
    title: "Release Notes",
    tagline: "Every Build Pack shipped, versioned.",
    desc: "Chronological log of what shipped, with BP numbers, engine versions, and links to the underlying playbooks.",
  },
];

export default function ResourcesIndex() {
  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="resources-index">
      <MarketingHeader />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0B1F3A] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(5,150,105,0.22),transparent_60%)]"></div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-1 rounded-full bg-white/10 border border-white/20 px-3 py-1 text-xs font-semibold">
              <Sparkles className="w-3 h-3 text-emerald-400" />
              Learn how the platform actually works
            </div>
            <h1 className="mt-6 text-4xl md:text-6xl font-bold tracking-tight leading-[1.05]">
              Docs, playbooks,<br />
              <span className="text-emerald-400">and real operator stories.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-2xl">
              We publish the same evidence bar internally as externally — every engine documented, every case study cited with numbers, every release logged with a Build Pack number.
            </p>
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="max-w-7xl mx-auto px-6 py-16 md:py-24">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {RESOURCES.map(({ icon: Icon, slug, title, tagline, desc, primary }) => (
            <Link
              key={slug}
              to={`/resources/${slug}`}
              data-testid={`resource-card-${slug}`}
              className={`group relative rounded-2xl border p-6 transition-all hover:shadow-xl ${
                primary
                  ? "border-emerald-500 bg-emerald-50 hover:border-emerald-600"
                  : "border-slate-200 bg-white hover:border-emerald-500"
              }`}
            >
              <Icon className="w-8 h-8 text-emerald-600" />
              <div className="mt-6 font-bold text-xl group-hover:text-emerald-700">
                {title}
              </div>
              <div className="mt-1 text-sm font-semibold text-emerald-700">
                {tagline}
              </div>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">{desc}</p>
              <div className="mt-6 text-sm font-semibold text-emerald-600 inline-flex items-center gap-1">
                Explore <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-[#0B1F3A] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(5,150,105,0.22),transparent_70%)]"></div>
        <div className="relative max-w-4xl mx-auto px-6 py-20 md:py-24 text-center">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
            Read what we build.<br />
            <span className="text-emerald-400">Then come build with us.</span>
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/auth?mode=signup"
              data-testid="resources-cta-primary"
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-6 py-3"
            >
              Start Free Trial <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-lg border border-white/20 hover:bg-white/5 text-white font-semibold px-6 py-3"
            >
              Book a live demo
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
